import React, { useState, useRef, useEffect } from 'react';
import { Grid, Typography, withStyles, Tooltip } from '@material-ui/core';
import { Link } from 'react-router-dom';

const Anchor = ({ link, text, classes }) => {
  return link.match(/\w+:\/\//) ? (
    <a href={link} target="_blank" rel="noopener noreferrer" className={classes.link}>
      {text}
    </a>
  ) : (
    <Link to={link} className={classes.link}>
      {text}
    </Link>
  );
};

// Utility function to truncate title with start...end format
const truncateTitle = (title, containerWidth) => {
  if (!title || !containerWidth) return { truncated: title, needsTruncation: false };

  const maxTitleWidth = containerWidth * 0.5;

  const tempElement = document.createElement('span');
  tempElement.style.position = 'absolute';
  tempElement.style.visibility = 'hidden';
  tempElement.style.fontSize = '18px';
  tempElement.style.fontFamily = 'Inter';
  tempElement.style.fontWeight = '500';
  tempElement.textContent = title;

  document.body.appendChild(tempElement);
  const titleWidth = tempElement.offsetWidth;
  document.body.removeChild(tempElement);

  if (titleWidth <= maxTitleWidth) {
    return { truncated: title, needsTruncation: false };
  }

  const ellipsisWidth = 20;
  const availableWidth = maxTitleWidth - ellipsisWidth;
  const charWidth = titleWidth / title.length;
  const availableChars = Math.floor(availableWidth / charWidth);

  if (availableChars <= 6) {
    return { truncated: title.substring(0, Math.max(3, availableChars)) + '...', needsTruncation: true };
  }

  const startChars = Math.ceil(availableChars * 0.6);
  const endChars = Math.floor(availableChars * 0.4);

  const startText = title.substring(0, startChars);
  const endText = title.substring(title.length - endChars);

  return {
    truncated: `${startText}...${endText}`,
    needsTruncation: true,
  };
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getHighlightedText = (textFragments, highlight, classes) => {
  if (!textFragments.length) {
    return null;
  }

  const textString = textFragments.reduce((searchResults, currentString) => {
    const punctuationRegex = /[.,:;!?]$/;
    if (punctuationRegex.test(currentString)) {
      return `${searchResults} ${currentString.slice(0, -1)} ...`;
    }
    return `${searchResults} ${currentString} ... `;
  }, '');

  if (!highlight || !highlight.trim()) {
    return <span>{textString}</span>;
  }

  const parts = textString.split(new RegExp(`(${escapeRegExp(highlight)})`, 'gi'));
  const highlightLower = highlight.toLowerCase();

  return (
    <span>
      {parts.map((part, i) => (
        <span
          key={`about_highlight_${i}`}
          className={part.toLowerCase() === highlightLower ? classes.highlightText : undefined}
        >
          {part}
        </span>
      ))}
    </span>
  );
};

const AboutCard = ({
  searchText, data, classes, index,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const cardRef = useRef(null);

  useEffect(() => {
    const measureWidth = () => {
      if (cardRef.current) {
        setContainerWidth(cardRef.current.offsetWidth);
      }
    };
    measureWidth();
    window.addEventListener('resize', measureWidth);
    return () => window.removeEventListener('resize', measureWidth);
  }, []);

  // Backend wraps OpenSearch highlight hits with `$` delimiters (GS_HIGHLIGHT_DELIMITER).
  const results = (data.text || []).map((result) => String(result).replaceAll('$', ''));
  const pagePath = data.page || '';
  const pageUrl = pagePath.match(/\w+:\/\//)
    ? pagePath
    : `${window.location.origin}${pagePath}`;
  const { truncated, needsTruncation } = truncateTitle(data.title, containerWidth);

  return (
    <Grid item container className={classes.card} id={`global_search_card_${index}`} ref={cardRef}>
      <Grid item xs className={classes.propertyContainer}>
        <div className={classes.titleRow}>
          <span className={classes.detailContainerHeader}>ABOUT</span>
          {needsTruncation ? (
            <Tooltip title={data.title} placement="top">
              <Typography variant="h3" className={classes.cardTitle}>
                {truncated}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="h3" className={classes.cardTitle}>
              {data.title}
            </Typography>
          )}
        </div>
        <div className={classes.cardBody}>
          <div className={classes.text}>{getHighlightedText(results, searchText, classes)}</div>
          {pagePath && (
            <div className={classes.linkText}>
              <Anchor link={pagePath} text={pageUrl} classes={classes} />
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

const styles = () => {
  const mdBreakpoint = '@media (min-width: 750px)';
  const lgBreakpoint = '@media (min-width: 1000px)';

  return {
    card: {
      '&:last-child $hrContainer': {
        display: 'none',
      },
      [lgBreakpoint]: {
        minWidth: '1047px',
        width: '1047px',
      },
      maxWidth: '800px',
      padding: '24px 16px 24px 20px',
      border: '0.25px solid #78AEB3',
      borderTopRightRadius: '20px',
      borderBottomLeftRadius: '20px',
      boxShadow: '0px 4px 20px 0px #00000040',
      marginBottom: '20px',
    },
    propertyContainer: {
      margin: '0px',
      padding: '0px',
    },
    indexContainer: {
      marginTop: '6px',
      fontFamily: 'Poppins',
      fontSize: '20px',
      fontWeight: '500',
      lineHeight: '22px',
      letterSpacing: '2%',
      textAlign: 'left',
      color: '#2E2E2E',
      width: '25px',
      marginRight: '20px',
      [mdBreakpoint]: {
        width: '49px',
      },
    },
    titleRow: {
      marginBottom: '5px',
      display: 'flex',
      alignItems: 'center',
    },
    detailContainerHeader: {
      margin: '0px',
      padding: '5px 15px 5px 15px',
      gap: '10px',
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '19.31px',
      letterSpacing: '0px',
      color: 'white',
      height: '30px',
      display: 'flex',
      borderRadius: '20px',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#BC8924',
      flexShrink: 0,
    },
    cardTitle: {
      margin: '0px',
      padding: '0px',
      marginTop: '2px',
      marginLeft: '10px',
      fontFamily: 'Inter',
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: '22px',
      letterSpacing: '0px',
      textAlign: 'left',
      color: '#00838F',
    },
    cardBody: {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '22px',
      letterSpacing: '2%',
      textAlign: 'left',
      margin: '0px',
      marginTop: '8px',
    },
    text: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      color: '#2E2E2E',
      margin: '0 0 10px 0',
    },
    highlightText: {
      color: '#05555C',
      fontWeight: 700,
    },
    linkText: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '15px',
      lineHeight: '24px',
    },
    link: {
      color: '#067781',
      textDecoration: 'none',
      fontFamily: 'Inter',
      fontSize: '15px',
      fontWeight: 400,
      lineHeight: '24px',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  };
};

export default withStyles(styles, { withTheme: true })(AboutCard);
